import axios from 'axios';
import { baseUrl } from '../hooks/useEndpoints';

/**
 * Client separato per le rotte pubbliche.
 *
 * L'istanza globale di axios ha interceptor che allegano il token, e che su
 * 401 tentano il refresh e poi rimandano al login. Qui il 401 è una risposta
 * legittima ("password errata"): con l'istanza globale l'atleta verrebbe
 * buttato sulla pagina di login invece di leggere l'errore. Questa istanza non
 * ha interceptor, quindi le risposte arrivano com'è giusto che siano.
 */
const publicClient = axios.create();

// Stessa chiave usata dall'interceptor globale (interceptor.ts). Serve per
// allegare a mano il bearer alle chiamate che DEVONO restare fuori
// dall'interceptor globale (me/redeemAsMe): con l'istanza globale un 401 su
// pagina pubblica /join farebbe `window.location.replace('/login')` e la
// schermata di ingresso non comparirebbe mai.
const ACCESS_TOKEN_KEY = 'leopart_access_token';
const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface IInvite {
  _id: string;
  code: string;
  teamId: string;
  label?: string;
  maxUses: number | null;
  usedCount: number;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  /** Perché il link non è utilizzabile; `null` se lo è. */
  state: 'not_found' | 'revoked' | 'expired' | 'full' | null;
  /** Quanti atleti sono entrati con questo link. */
  redemptions: number;
}

export interface IInviteRedemption {
  _id: string;
  userId: string;
  email: string;
  existingAccount: boolean;
  createdAt: string;
}

export interface IInvitePreview {
  valid: boolean;
  reason?: 'not_found' | 'revoked' | 'expired' | 'full';
  teamName?: string;
  remainingUses?: number | null;
  expiresAt?: string | null;
}

export interface IRedeemResult {
  userId: string;
  teamId: string;
  teamName: string;
  existingAccount: boolean;
  alreadyMember: boolean;
}

export interface ICreateInviteBody {
  teamId: string;
  label?: string;
  maxUses?: number;
  expiresInDays?: number;
}

export interface IRedeemBody {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  birthdayDate?: string;
  gender?: string;
  position?: string;
}

/** Profilo di chi è connesso, per la schermata di ingresso rapido. */
export interface ICurrentUser {
  email: string;
  firstName?: string;
  lastName?: string;
}

export const invitesEndpoints = {
  /**
   * Chi sono: risponde solo se in sessione c'è un token valido.
   * Usa `publicClient` (niente redirect al login su 401) + bearer a mano: sulla
   * pagina pubblica /join l'atleta NON loggato riceve 401 legittimo — va gestito
   * come "nessuna sessione", non come "vai al login".
   */
  me: () =>
    publicClient.get<ICurrentUser>(`${baseUrl}/auth/me`, {
      headers: authHeader(),
    }),

  /** Riscatto per utente già connesso: l'identità la stabilisce il token. */
  redeemAsMe: (code: string) =>
    publicClient.post<IRedeemResult>(
      `${baseUrl}/invites/${code}/redeem-as-me`,
      {},
      { headers: authHeader() },
    ),

  list: (teamId: string) =>
    axios.get<IInvite[]>(`${baseUrl}/invites`, { params: { teamId } }),

  create: (body: ICreateInviteBody) =>
    axios.post<IInvite>(`${baseUrl}/invites`, body),

  revoke: (id: string, revoked: boolean) =>
    axios.patch<IInvite>(`${baseUrl}/invites/${id}/revoke`, { revoked }),

  redemptions: (id: string) =>
    axios.get<IInviteRedemption[]>(`${baseUrl}/invites/${id}/redemptions`),

  // ─── pubbliche: nessun token, nessun header entity-id ────────────────
  preview: (code: string) =>
    publicClient.get<IInvitePreview>(`${baseUrl}/invites/public/${code}`),

  redeem: (code: string, body: IRedeemBody) =>
    publicClient.post<IRedeemResult>(
      `${baseUrl}/invites/public/${code}/redeem`,
      body,
    ),
};

/** URL completo da consegnare all'atleta. */
export const buildInviteUrl = (code: string) =>
  `${window.location.origin}/join/${code}`;
